import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import type { StickyContext, StickyElement } from './sticky.interface';

@Injectable({ providedIn: 'root' })
export class StickyService {
  private activeTop: StickyElement[] = [];
  private activeBottom: StickyElement[] = [];
  private _topStackHeightSubject$ = new Subject<void>();
  private _bottomStackHeightSubject$ = new Subject<void>();

  add({
    element,
    isStickyTop,
    order,
    context = 'global',
  }: {
    element: HTMLElement;
    isStickyTop: boolean;
    order: number;
    context?: StickyContext;
  }): void {
    if (isStickyTop) {
      this.activeTop = this.addActiveElement({
        activeElements: this.activeTop,
        element,
        order,
        context,
      });
    } else {
      this.activeBottom = this.addActiveElement({
        activeElements: this.activeBottom,
        element,
        order,
        context,
      });
    }
  }

  remove(element: HTMLElement, isStickyTop: boolean): void {
    if (isStickyTop) {
      this.activeTop = this.activeTop.filter(item => item.element !== element);
    } else {
      this.activeBottom = this.activeBottom.filter(
        item => item.element !== element,
      );
    }
  }

  getOffset({
    target,
    isStickyTop,
    context = 'global',
  }: {
    target: HTMLElement;
    isStickyTop: boolean;
    context?: StickyContext;
  }): number {
    const activeElements = isStickyTop ? this.activeTop : this.activeBottom;
    let offset = 0;
    let index;

    for (index = 0; index < activeElements.length; index++) {
      const item = activeElements[index];

      // Only consider elements in the same context
      if (item.context === context) {
        if (item.element === target) {
          return offset;
        }

        offset += item.element.offsetHeight;
      }
    }

    return offset;
  }

  isTopmost({
    element,
    isStickyTop,
    context = 'global',
  }: {
    element: HTMLElement;
    isStickyTop: boolean;
    context?: StickyContext;
  }): boolean {
    if (!isStickyTop) {
      return false;
    }

    const firstInContext = this.activeTop.find(
      item => item.context === context,
    );
    return firstInContext ? firstInContext.element === element : false;
  }

  stackHeightChange(isStickyTop: boolean): Observable<void> {
    return (
      isStickyTop
        ? this._topStackHeightSubject$.asObservable()
        : this._bottomStackHeightSubject$.asObservable()
    ).pipe(auditTime(0));
  }

  /**
   * Notify that stack heights have changed
   * This allows elements to recalculate their offsets
   */
  notifyStackHeightChange(isStickyTop: boolean): void {
    if (isStickyTop) {
      this._topStackHeightSubject$.next();
    } else {
      this._bottomStackHeightSubject$.next();
    }
  }

  private addActiveElement({
    activeElements,
    element,
    order,
    context = 'global',
  }: {
    activeElements: StickyElement[];
    element: HTMLElement;
    order: number;
    context?: StickyContext;
  }): StickyElement[] {
    const position = order !== undefined ? order : activeElements.length;
    activeElements.splice(position, 0, { element, context });

    return activeElements;
  }
}
